import { useQueryClient } from '@tanstack/react-query';
import { listen, type UnlistenFn } from '@tauri-apps/api/event';
import { useStore } from 'jotai';
import { useEffect, useRef } from 'react';
import { type IntlShape, useIntl } from 'react-intl';

import { chronicleSessionIdAtom, isChronicleConnectedAtom } from '@/atoms/chronicle';
import { offlineActiveRecordingIdAtom } from '@/atoms/offline-recording/sync';
import { isRecordingInProgress } from '@/hooks/desktop/useChronicleSessionControl';
import { chronicleBridge } from '@/tauri/chronicle';
import { tauriWindowController } from '@/tauri/controller/window';
import { offlineRecordingBridge } from '@/tauri/offline-recording/recording';
import { isTauri } from '@/tauri/utils';
import { logger } from '@/utils/logger/logger';
import { toast } from '@heidi/design-system/toast';
import { surfaceTauriWindow } from '@heidi/plugin-core';
import { getCurrentSessionId } from '@heidi/runtime/shell';
import { findActiveSessionFromCache } from '@heidi/session';

const CLOSE_REQUESTED_EVENT = 'app://close-requested';

/**
 * Serialize `set_close_guard_ready` toggles. Mount fires `(true)` and cleanup
 * fires `(false)` as independent fire-and-forget invokes. On a remount (effect
 * deps changing identity, or React 18 StrictMode in dev), the old cleanup's
 * `false` can be processed by Rust *after* the new mount's `true`, leaving
 * `CLOSE_GUARD_READY=false` while a listener is live — Rust then fails open and
 * never traps a close mid-recording. Chaining the invokes guarantees Rust sees
 * them in issue order, so the last-issued (currently-mounted) state wins.
 */
let closeGuardReadyChain: Promise<unknown> = Promise.resolve();
function setCloseGuardReady(ready: boolean): void {
  closeGuardReadyChain = closeGuardReadyChain
    .catch(() => undefined)
    .then(() => tauriWindowController.set_close_guard_ready(ready))
    .catch((error) => {
      logger.error(`[close-guard] set_close_guard_ready(${ready}) failed`, error);
    });
}

/**
 * Show the native OS confirmation dialog (NSAlert on macOS, MessageBox on
 * Windows). Returns true if the user confirmed the close.
 *
 * react-intl owns the copy — Tauri/Rust must never carry user-facing text.
 */
async function confirmCloseWhileRecording(intl: IntlShape): Promise<boolean> {
  const { ask } = await import('@tauri-apps/plugin-dialog');
  return ask(
    intl.formatMessage({
      defaultMessage:
        'Your session is still recording. Closing now may lose your progress.',
      id: 'hTkhW03Mox',
    }),
    {
      title: intl.formatMessage({ defaultMessage: 'Close Heidi?', id: '0/D2B5Rjvi' }),
      kind: 'warning',
      okLabel: intl.formatMessage({ defaultMessage: 'Close Heidi', id: 'wWGlE7FSXG' }),
      cancelLabel: intl.formatMessage({ defaultMessage: 'Cancel', id: '47FYwba+bI' }),
    }
  );
}

/**
 * How long Rust's `offline_recording_stop` can legitimately take, as a sum of
 * the budgets the native side actually enforces. This is a wedge detector, not
 * a schedule: the thing that releases the quit is the invoke settling, and this
 * only bounds the case where it never does.
 *
 * Sized rather than guessed, because the previous 3s guess was shorter than the
 * native path and would cut a healthy stop short — the exact data loss this
 * whole guard exists to prevent:
 *
 * - `RecordingHandle::stop` (`src-tauri/src/audio/record/types.rs`) joins the
 *   producer thread on a 2s budget, then the WAV writer thread on
 *   `writer_join_timeout(write_timeout)` = `3 * write_timeout + 2s`. An offline
 *   capture starts with `RecordingConfig::default()`, whose `write_timeout` is
 *   15s, so that join alone is 47s. The writer can be inside an in-flight
 *   flush, then still has to do a final flush and finalize the WAV header —
 *   three write windows — and under disk pressure it uses them.
 * - Only then does `finalize_take` join the segments and AES-GCM encrypt them
 *   on `spawn_blocking`. That work is proportional to consult length and is not
 *   itself bounded, so it gets explicit headroom rather than being ignored.
 *
 * Raising `write_timeout` on the Rust side without raising this re-opens the
 * same gap. A number restated across a language boundary is not checked by
 * either side, so the writer-join term below is an explicit mirror, pinned
 * against the real declaration by the co-located native-budget test — it parses
 * the Rust and fails on the machine that changes it.
 */
const NATIVE_PRODUCER_JOIN_MS = 2_000;
/** Mirror of `writer_join_timeout(RecordingConfig::default().write_timeout)`. */
export const NATIVE_WRITER_JOIN_MIRROR_MS = 47_000;
const NATIVE_FINALIZE_HEADROOM_MS = 30_000;
export const OFFLINE_CAPTURE_STOP_TIMEOUT_MS =
  NATIVE_PRODUCER_JOIN_MS + NATIVE_WRITER_JOIN_MIRROR_MS + NATIVE_FINALIZE_HEADROOM_MS;

/**
 * Finalize a live offline capture before the process is killed.
 *
 * An offline capture only becomes durable when Rust's `stop()` runs — that is
 * the sole writer of the sync-tracking row that makes the take resumable and
 * syncable. A pause writes nothing, so a paused-then-quit consult would
 * otherwise depend on the boot-time salvage path, which gives up quietly when
 * the per-user key is unavailable or the audio header is unreadable, after
 * which the temp tree is wiped and the audio is gone. Turning a deliberate quit
 * into a clean stop keeps it off that path entirely.
 *
 * Best-effort and bounded, mirroring the Chronicle device stop: a clinician
 * must always be able to quit, so a stop that rejects or hangs is logged and
 * the close proceeds anyway. No live capture is a clean no-op.
 *
 * Reads no flag itself: the umbrella gate is inherited from
 * `offlineActiveRecordingIdAtom`, whose only writers are `useOfflineRecorder`'s
 * `start()` (reached solely via `canOfflineCapture`, which carries the flag)
 * and the `capture_started` event Rust emits after that same start. With the
 * flag off the id stays null and quit issues no IPC — an invariant a new,
 * ungated writer of that atom would break here rather than at its own site.
 */
async function stopActiveOfflineCapture(activeCaptureId: string | null): Promise<void> {
  if (!activeCaptureId) return;

  logger.info('[close-guard] Finalizing offline capture before close', {
    localSessionId: activeCaptureId,
  });
  // The timer is a safety net, not a schedule, so it is cancelled the moment
  // the stop settles. Left running it would log a false "did not settle" alarm
  // after a perfectly good stop, and — because a close can fail and leave the
  // app running — repeated closes would each strand another live timer holding
  // its callback.
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  try {
    await Promise.race([
      offlineRecordingBridge.stop(activeCaptureId).catch((error) => {
        logger.error('[close-guard] Offline capture stop failed before close', error);
      }),
      new Promise<void>((resolve) => {
        timeoutId = setTimeout(() => {
          logger.error(
            '[close-guard] Offline capture stop did not settle in time — closing anyway'
          );
          resolve();
        }, OFFLINE_CAPTURE_STOP_TIMEOUT_MS);
      }),
    ]);
  } finally {
    clearTimeout(timeoutId);
  }
}

function showCloseFailureToast(intl: IntlShape): void {
  toast({
    title: intl.formatMessage({
      defaultMessage: "Couldn't close Heidi. Please try again.",
      id: 'qCgV/36Jv6',
    }),
    tone: 'destructive',
  });
}

export function useCloseAppGuard(): void {
  const queryClient = useQueryClient();
  const jotaiStore = useStore();
  // intl is read inside the listener via a ref so a locale change doesn't
  // force the event listener to be torn down and re-registered.
  const intl = useIntl();
  const intlRef = useRef(intl);
  intlRef.current = intl;

  useEffect(() => {
    if (!isTauri()) return;

    let unlisten: UnlistenFn | undefined;
    let cancelled = false;
    // Guard against stacking dialogs if a second close request arrives
    // while the first dialog is still open (e.g. rapid Cmd+Q presses).
    let handling = false;

    const handleCloseRequested = async () => {
      if (handling) return;
      handling = true;
      try {
        // Reuse the same "is any recording in progress?" helper Chronicle
        // uses for live-vs-offline routing. Its three layers (Redux timer +
        // chronicleSessionId + query cache) cover desktop mic, Heidi Remote,
        // Haiku, and mobile-offline. Reading useSessionRecordingStore.status
        // alone misses the desktop case (Tauri uses the V1 recording path,
        // which never sets that store). Dictation is intentionally excluded.
        const sessionId = getCurrentSessionId() ?? '';
        const chronicleSessionId = jotaiStore.get(chronicleSessionIdAtom);
        const result = isRecordingInProgress({
          sessionId,
          chronicleSessionId,
          queryClient,
        });

        // The route-based lookup above misses a paused (or recording)
        // session the user has navigated away from. Scan the query cache
        // for any RECORDING/PAUSED session as a route-independent fallback
        // so those aren't silently force-closed.
        const cachedActiveSession = result.inProgress
          ? null
          : findActiveSessionFromCache(queryClient);

        // Read once, up front: both close paths below need it, and this is the
        // only app-wide record of which offline capture is live.
        const activeOfflineCaptureId = jotaiStore.get(offlineActiveRecordingIdAtom);

        if (!result.inProgress && !cachedActiveSession) {
          logger.info(
            '[close-guard] close requested with no recording in progress — force-closing'
          );
          // This is the branch a PAUSED offline capture actually lands in: an
          // offline pause stops the Redux timer and never touches the server
          // session, so none of the three layers above see it. Finalize it here
          // or the take is left to the crash-salvage path.
          await stopActiveOfflineCapture(activeOfflineCaptureId);
          await tauriWindowController.force_close_main_window();
          return;
        }

        logger.info('[close-guard] Recording in progress — confirming close', {
          reason: result.inProgress
            ? result.reason
            : `cached ${cachedActiveSession?.recordingStatus} session ${cachedActiveSession?.sessionId}`,
        });
        // A native dialog opened over a hidden/minimized window cannot be
        // answered. Treat a failed surface attempt as Cancel; `finally` then
        // releases `handling`, so a later close request is not swallowed.
        const surfaced = await surfaceTauriWindow();
        if (!surfaced) {
          logger.warn(
            '[close-guard] Window could not be surfaced — treating close as Cancel'
          );
          showCloseFailureToast(intlRef.current);
          return;
        }
        const confirmed = await confirmCloseWhileRecording(intlRef.current);
        if (confirmed) {
          // Only a connected Heidi Remote can orphan a device-side stream, so
          // gate the device stop on it — most users (desktop mic, Haiku,
          // mobile-offline) have no Remote and shouldn't trigger a Chronicle IPC.
          // When one IS connected, best-effort tell it to stop before we kill the
          // process so a recording isn't left running on the device (APP-7571):
          // the firmware keeps recording until an explicit APP&STO, and force-close
          // otherwise never sends one. Bounded so a flaky BLE link can't hang the quit.
          if (jotaiStore.get(isChronicleConnectedAtom)) {
            await Promise.race([
              chronicleBridge.stopRecording().catch((error) => {
                logger.warn(
                  '[close-guard] Best-effort Chronicle device stop failed',
                  error
                );
              }),
              new Promise<void>((resolve) => {
                setTimeout(resolve, 1500);
              }),
            ]);
          }
          // Same reason as the no-recording branch above, for a capture that
          // was still running rather than paused.
          await stopActiveOfflineCapture(activeOfflineCaptureId);
          await tauriWindowController.force_close_main_window();
        }
      } catch (error) {
        logger.error('[close-guard] Failed to handle close request', error);
        // Surface feedback so a failed close (e.g. a force_close_main_window
        // IPC rejection) isn't silent — on Windows/Linux the OS close was
        // already prevented, so without this the window can appear stuck with
        // only a log line.
        showCloseFailureToast(intlRef.current);
      } finally {
        handling = false;
      }
    };

    void (async () => {
      try {
        const handle = await listen(CLOSE_REQUESTED_EVENT, () => {
          void handleCloseRequested();
        });
        if (cancelled) {
          handle();
          return;
        }
        unlisten = handle;
        // Only now that the listener is live do we tell Rust to start
        // intercepting closes. Before this, Rust fails open (native close).
        setCloseGuardReady(true);
      } catch (error) {
        logger.error('[close-guard] Failed to register close listener', error);
      }
    })();

    return () => {
      cancelled = true;
      unlisten?.();
      // Revert Rust to fail-open while no listener is mounted.
      setCloseGuardReady(false);
    };
  }, [queryClient, jotaiStore]);
}
